import type {
  OnboardingTourStepOption,
  SupportedOnboardingTourStep,
  UpdateOnboardingTourStepOptionHandler,
} from 'models';
import { useMemo } from 'react';

type NavigateThroughStepsOptions = {
  onNavigateToNextStep?: () => void;
  onNavigateToPreviousStep?: () => void;
};

const useNavigateToPreviousStep = ({
  onChangeStep,
  step,
  steps,
}: {
  onChangeStep: UpdateOnboardingTourStepOptionHandler;
  step: OnboardingTourStepOption;
  steps: SupportedOnboardingTourStep[];
}) =>
  useMemo(() => {
    const currentStepIndex = steps.findIndex((supportedStep) => supportedStep.step === step);
    const previousStep = steps[currentStepIndex - 1];

    if (currentStepIndex > 0 && previousStep) {
      return () => {
        onChangeStep({ step: previousStep.step });
      };
    }

    if (currentStepIndex === 0) {
      return () => {
        onChangeStep({ step: 'intro' });
      };
    }

    return undefined;
  }, [onChangeStep, step, steps]);

const useNavigateToNextStep = ({
  onChangeStep,
  step,
  steps,
}: {
  onChangeStep: UpdateOnboardingTourStepOptionHandler;
  step: OnboardingTourStepOption;
  steps: SupportedOnboardingTourStep[];
}) =>
  useMemo(() => {
    if (step === 'intro') {
      const firstStep = steps[0];

      if (!firstStep) {
        return () => {
          onChangeStep({ step: 'finish' });
        };
      }

      return () => {
        onChangeStep({ step: firstStep.step });
      };
    }

    const currentStepIndex = steps.findIndex((supportedStep) => supportedStep.step === step);
    const nextStep = steps[currentStepIndex + 1];

    if (currentStepIndex === steps.length - 1) {
      return () => {
        onChangeStep({ step: 'finish' });
      };
    }

    if (nextStep) {
      return () => {
        onChangeStep({ step: nextStep.step });
      };
    }

    return undefined;
  }, [onChangeStep, step, steps]);

export const useNavigateThroughOnboardingTourSteps = ({
  onChangeStep,
  step,
  steps,
}: {
  onChangeStep: UpdateOnboardingTourStepOptionHandler;
  step: OnboardingTourStepOption;
  steps: SupportedOnboardingTourStep[];
}) => {
  const onNavigateToPreviousStep = useNavigateToPreviousStep({ onChangeStep, step, steps });
  const onNavigateToNextStep = useNavigateToNextStep({ onChangeStep, step, steps });

  return useMemo<NavigateThroughStepsOptions>(
    () => ({
      onNavigateToNextStep,
      onNavigateToPreviousStep,
    }),
    [onNavigateToNextStep, onNavigateToPreviousStep],
  );
};
